import type { EnumLabel } from "../../../fields/utils";
import type { DataTableInstance } from "../table-features";
import type {
  BoolFilterField,
  FilterField,
  MultiFilterField,
  TextFilterField,
  TextFilterOperator,
  TextFilterValue,
} from "./types";

import { sameFilterValue } from "../filter-fns";

const DEFAULT_TEXT: TextFilterValue = { query: "", operator: "contains" };

export function resolveOptions<T, V>(
  field: MultiFilterField<T, V>,
  table: DataTableInstance<T>,
): Map<V, EnumLabel> {
  return typeof field.options === "function"
    ? field.options(table)
    : field.options;
}

export function getTextValue<T>(
  table: DataTableInstance<T>,
  field: TextFilterField<T>,
): TextFilterValue {
  const raw = table.getColumn(field.id)?.getFilterValue() as
    | TextFilterValue
    | undefined;
  return raw ?? DEFAULT_TEXT;
}

export function setText<T>(
  table: DataTableInstance<T>,
  field: TextFilterField<T>,
  query: string,
) {
  const col = table.getColumn(field.id);
  if (!col) return;
  const current =
    (col.getFilterValue() as TextFilterValue | undefined) ?? DEFAULT_TEXT;
  if (!query) {
    col.setFilterValue(undefined);
  } else {
    col.setFilterValue({ query, operator: current.operator });
  }
}

export function getOperator<T>(
  table: DataTableInstance<T>,
  field: TextFilterField<T>,
): TextFilterOperator {
  return getTextValue(table, field).operator;
}

export function setOperator<T>(
  table: DataTableInstance<T>,
  field: TextFilterField<T>,
  operator: TextFilterOperator,
) {
  const col = table.getColumn(field.id);
  if (!col) return;
  const current =
    (col.getFilterValue() as TextFilterValue | undefined) ?? DEFAULT_TEXT;
  col.setFilterValue({ query: current.query, operator });
}

export function getMultiSelected<T, V>(
  table: DataTableInstance<T>,
  field: MultiFilterField<T, V>,
): V[] {
  const raw = table.getColumn(field.id)?.getFilterValue() as V[] | undefined;
  return raw ? raw.slice() : [];
}

export function toggleMulti<T, V>(
  table: DataTableInstance<T>,
  field: MultiFilterField<T, V>,
  value: V,
) {
  const col = table.getColumn(field.id);
  if (!col) return;
  const current = ((col.getFilterValue() as V[] | undefined) ?? []).slice();
  // An initial numeric-enum filter stores numbers while the toggled option key
  // is a string, so a strict indexOf would never find it and would wrongly
  // append a duplicate instead of unchecking — match via sameFilterValue.
  const idx = current.findIndex((v) => sameFilterValue(v, value));
  if (idx === -1) current.push(value);
  else current.splice(idx, 1);
  col.setFilterValue(current.length > 0 ? current : undefined);
}

export function getBoolSelected<T>(
  table: DataTableInstance<T>,
  field: BoolFilterField<T>,
): boolean {
  return table.getColumn(field.id)?.getFilterValue() === true;
}

export function setBool<T>(
  table: DataTableInstance<T>,
  field: BoolFilterField<T>,
  on: boolean,
) {
  const col = table.getColumn(field.id);
  if (!col) return;
  col.setFilterValue(on ? true : undefined);
}

export function isFieldActive<T>(
  table: DataTableInstance<T>,
  field: FilterField<T>,
): boolean {
  if (field.kind === "text") {
    return getTextValue(table, field).query.length > 0;
  }
  if (field.kind === "multi") {
    return getMultiSelected(table, field).length > 0;
  }
  return getBoolSelected(table, field);
}

export function getActiveCount<T>(
  table: DataTableInstance<T>,
  fields: FilterField<T>[],
): number {
  let n = 0;
  for (const field of fields) {
    if (isFieldActive(table, field)) n++;
  }
  return n;
}

export function clearField<T>(
  table: DataTableInstance<T>,
  field: FilterField<T>,
) {
  table.getColumn(field.id)?.setFilterValue(undefined);
}

export function clearAll<T>(
  table: DataTableInstance<T>,
  fields: FilterField<T>[],
) {
  for (const field of fields) {
    if (isFieldActive(table, field)) clearField(table, field);
  }
}
